import type { AxiosRequestConfig } from 'axios';
import { client } from './client';
import { HTTP_TYPES } from '@/lib/enums';
import { useUserStore } from '@/stores/user.store';
import { useNotificationsStore } from '@/stores/notifications.store';
import { msalInstance } from '@/util/authConfig';
import { isPlatformUrl } from '@/api/routes/onetwofive/platform';
import {
	AUTH_MESSAGES,
	FORBIDDEN_NOTIFICATION_KEY,
	getHttpStatus,
	isForbidden,
	isUnauthorized,
} from '@/util/authErrors';
import { logAuthFailure } from '@/util/logger';

const EXPIRY_SKEW_MS = 60_000;

const isFresh = (token: string | null, expiresOn: number | null): boolean => {
	if (!token) {
		return false;
	}

	// No expiry recorded means we cannot tell it is stale, so treat it as usable and
	// let a 401 correct us. Same call the depot path has always made.
	if (!expiresOn) {
		return true;
	}

	return Date.now() < expiresOn - EXPIRY_SKEW_MS;
};

/** True when we currently hold a depot token that still looks valid. */
const hasUsableToken = (): boolean => {
	const userStore = useUserStore();

	return isFresh(userStore.accessToken, userStore.accessTokenExpiresOn);
};

/** True when we currently hold a 125 platform token that still looks valid. */
const hasUsablePlatformToken = (): boolean => {
	const userStore = useUserStore();

	return isFresh(userStore.platformToken, userStore.platformTokenExpiresOn);
};

/**
 * The bearer credential for `url`.
 *
 * The 125 platform origin is authorised by the platform token; every other host,
 * depot and chatbot alike, keeps the depot token it has always had. Refreshes on
 * demand, because sending a known-stale token buys a guaranteed 401 plus a retry
 * when refreshing first costs one call.
 *
 * Returns '' when no token could be obtained, and leaves it to the caller to decide
 * what that means — the two resources fail very differently.
 */
const bearerFor = async (url: string): Promise<string> => {
	const userStore = useUserStore();

	if (isPlatformUrl(url)) {
		if (!hasUsablePlatformToken()) {
			await userStore.ensurePlatformToken(true);
		}

		return userStore.platformToken ? `Bearer ${userStore.platformToken}` : '';
	}

	if (!hasUsableToken()) {
		await userStore.ensureAccessToken(false);
	}

	return userStore.accessToken ? `Bearer ${userStore.accessToken}` : '';
};

/**
 * Centralized handling for authorization responses (401 / 403). This is the single
 * place where the HTTP layer decides whether a failure means "your session is gone,
 * re-authenticate" versus "you simply aren't allowed to do this".
 *
 * Returns `true` when the caller should retry the request (a fresh token was obtained).
 */
const handleAuthResponse = async (
	status: number,
	url: string,
	alreadyRetried: boolean,
): Promise<boolean> => {
	const userStore = useUserStore();

	// The 125 platform is a separate resource with a separate token, so neither of
	// the recoveries below applies to it: refreshing the depot token cannot fix a
	// platform 401, and a dead platform token is not a dead session — the depot half
	// of the app carries on — so it must never reach `requireReauthentication`.
	const isPlatform = isPlatformUrl(url);

	if (isUnauthorized(status)) {
		logAuthFailure('api_unauthorized', { status, url });

		if (isPlatform) {
			// One forced refresh of the PLATFORM token, then give up quietly.
			// `ensurePlatformToken` has already notified if it failed.
			if (!alreadyRetried && (await userStore.ensurePlatformToken(true))) {
				return true;
			}

			return false;
		}

		// First 401: try one silent refresh and let the caller retry once.
		if (!alreadyRetried) {
			const token = await userStore.ensureAccessToken(true);
			if (token) {
				return true;
			}
		}

		// Refresh failed or the retry still came back 401 → interactive re-auth.
		userStore.requireReauthentication();
		return false;
	}

	if (isForbidden(status)) {
		// A 403 with a missing/expired token is a session problem, not a permission
		// problem — route it through re-auth. Otherwise it's a genuine authorization
		// denial: tell the user clearly but DON'T trigger a re-login loop.
		const tokenIsUsable = isPlatform ? hasUsablePlatformToken() : hasUsableToken();

		if (!tokenIsUsable) {
			logAuthFailure('api_forbidden', { status, url, reason: 'token_invalid' });

			if (!isPlatform) {
				userStore.requireReauthentication();
			}

			return false;
		}

		logAuthFailure('api_forbidden', { status, url, reason: 'insufficient_permissions' });
		useNotificationsStore().notify({
			type: 'warning',
			title: AUTH_MESSAGES.forbiddenTitle,
			message: AUTH_MESSAGES.forbiddenBody,
			dedupeKey: FORBIDDEN_NOTIFICATION_KEY,
		});
		return false;
	}

	return false;
};

export default async function apiCall(
	method: string,
	url: string,
	data?: any,
	params?: any,
	type: string = 'application/json',
	apiVersion: string = 'v1',
	headers: Record<string, string> = {},
	onChunk?: (chunk: string) => void,
	signal?: AbortSignal,
	_isRetry: boolean = false,
): Promise<any> {
	const fullUrl = url + (params ? '?' + new URLSearchParams(params).toString() : '');

	const userStore = useUserStore();
	// Expiry-aware, not just presence-aware, and resource-aware: the 125 platform
	// origin is authorised by its own token, everything else by the depot one.
	const authorization = await bearerFor(fullUrl);

	// Omitted entirely rather than sent empty: an `Authorization: ` header is malformed
	// and a gateway may reject it outright, which would look like an auth failure.
	const authHeader: Record<string, string> = authorization
		? { Authorization: authorization }
		: {};

	if (authorization === '') {
		if (isPlatformUrl(fullUrl)) {
			// `ensurePlatformToken` has already logged and told the user the 125
			// section is unavailable. A missing platform token is NOT a dead session,
			// so this must not prompt re-auth — the depot side is still fine.
			logAuthFailure('api_unauthorized', { url, reason: 'no_platform_token' });

			return null;
		}

		// No usable token. If MSAL still knows about the account the session has
		// gone stale and we must prompt re-auth; either way the failure is surfaced
		// rather than swallowed.
		logAuthFailure('api_unauthorized', { url, reason: 'no_token' });
		if (msalInstance.getActiveAccount()) {
			userStore.requireReauthentication();
		}

		return null;
	}

	if (onChunk) {
		// A FormData body must be sent as-is with NO explicit Content-Type so the
		// browser can set the multipart boundary. Non-FormData bodies keep the
		// existing JSON behaviour, so the JSON streamers are unaffected.
		const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;

		const streamHeaders: Record<string, string> = {
			...authHeader,
			'X-Api-Version': apiVersion,
			Accept: type,
			...headers,
		};

		if (!isFormData) {
			streamHeaders['Content-Type'] = type;
		}

		const response = await fetch(fullUrl, {
			method: method === HTTP_TYPES.POST ? 'POST' : 'GET',
			headers: streamHeaders,
			body:
				method === HTTP_TYPES.POST
					? isFormData
						? (data as FormData)
						: JSON.stringify(data)
					: undefined,
			signal,
		});

		if (response.status === 401 || response.status === 403) {
			const shouldRetry = await handleAuthResponse(response.status, fullUrl, _isRetry);
			if (shouldRetry) {
				return apiCall(
					method,
					url,
					data,
					params,
					type,
					apiVersion,
					headers,
					onChunk,
					signal,
					true,
				);
			}

			throw new Error(`Request failed: ${response.status}`);
		}

		if (!response.ok || !response.body) {
			throw new Error(`Request failed: ${response.status}`);
		}

		const reader = response.body.getReader();
		const decoder = new TextDecoder();

		let done = false;
		while (!done) {
			const result = await reader.read();
			done = result.done;
			if (result.value) {
				onChunk(decoder.decode(result.value, { stream: true }));
			}
		}

		return;
	}

	try {
		let response;

		if (method !== HTTP_TYPES.POST) {
			// Dispatch on `method` rather than calling client.get(): every non-POST verb
			// used to be sent as a GET, so DELETE/PUT silently hit the read endpoint and
			// came back 200 — a delete that looked like it worked and changed nothing.
			const config: AxiosRequestConfig = {
				method,
				url: fullUrl,
				data,
				headers: {
					...authHeader,
					'X-Api-Version': apiVersion,
					Accept: type,
					...headers,
				},
			};

			response = await client.request(config);
		} else {
			// Same rule as the streaming branch above: a FormData body must be sent with
			// NO explicit Content-Type. Axios only generates the `multipart/form-data`
			// header (and its boundary) when none is already set, and without that
			// boundary the body is unparseable server-side. Accept / Authorization /
			// X-Api-Version are unaffected, so a multipart POST can still ask for a
			// non-JSON response such as text/csv.
			const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;

			response = await client.post(fullUrl, data, {
				headers: {
					...authHeader,
					...(isFormData ? {} : { 'Content-Type': type }),
					'X-Api-Version': apiVersion,
					Accept: type,
					...headers,
				},
			});
		}

		if (!(response.status >= 200 && response.status <= 299)) {
			console.error(`API call failed with status code: ${response.status}`, response);
		}

		return response;
	} catch (err: any) {
		const status = getHttpStatus(err);

		// Funnel authorization failures through the centralized handler so they are
		// never silently ignored.
		if (status === 401 || status === 403) {
			const shouldRetry = await handleAuthResponse(status, fullUrl, _isRetry);
			if (shouldRetry) {
				return apiCall(
					method,
					url,
					data,
					params,
					type,
					apiVersion,
					headers,
					onChunk,
					signal,
					true,
				);
			}

			return err;
		}

		const code = err.response?.data?.error?.code;
		console.error(`API call failed with code: ${code}`, err);
		// @ts-ignore
		return err;
	}
}
